import * as React from 'react';
import {DeprecatedPopoverBridge} from '@packages/systems/design-system/PopoverBridge';

import {Box, collides} from '@packages/utilities/box';
import {ThemeContext, designerTheme} from '@packages/utilities/styled';

import {useCalloutGate} from './CalloutGateContext';
import type {CalloutPopoverBridgeProps} from './types';

const NOOP_ANCHOR_REF = React.createRef<HTMLElement>();

/**
 * A bridge component to migrate legacy Popovers off of findDomNode APIs so we can upgrade to React 19.
 */
export const CalloutPopoverBridge = React.memo<CalloutPopoverBridgeProps>(
  function CalloutPopoverBridge({
    children,
    animate,
    content,
    open,
    applyOffset,
    placement,
    hotspot = 'none',
    hotspotConfig,
    mask = 'FULL',
    onMaskClick,
    canvas,
    canvasViewport,
    parentBox,
    onChange,
    forcePlacement = false,
    asChild,
  }) {
    const isCalloutAllowed = useCalloutGate();

    const renderPopoverContent = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
      (props) =>
        content ? (
          <ThemeContext.Provider value={designerTheme}>
            {content(props)}
          </ThemeContext.Provider>
        ) : null,
      [content]
    );

    function createMasks(_: Box, anchor: Box) {
      // No mask, just the popover. Everything is interactive
      if (mask === 'NONE') {
        return [];
      }

      const masksConfigs = [
        ...getHotspotConfigs({
          anchor,
          animate,
          hotspot,
          // By default, use onMaskClick. If a separate onClick is required for the hotspot,
          // it can be passed in via hotspotConfig which will override onMaskClick.
          hotspotConfig: {
            onClick: onMaskClick,
            ...hotspotConfig,
          },
        }),
      ];

      if (typeof mask === 'string') {
        masksConfigs.push(
          ...getCanvasConfigs({
            mask,
            hotspot,
            anchor,
            canvas,
            canvasViewport,
            parentBox,
            onClick: onMaskClick,
          })
        );
      } else if (Array.isArray(mask)) {
        masksConfigs.push(...mask);
      }
      return masksConfigs.length > 0 ? masksConfigs : defaultMaskConfig;
    }

    if (!isCalloutAllowed) {
      // Render only the anchor children; skip the popover.
      return asChild ? <>{children}</> : <>{children(NOOP_ANCHOR_REF)}</>;
    }

    return (
      <DeprecatedPopoverBridge
        applyOffset={applyOffset}
        animate={animate}
        state={{open}}
        placement={placement}
        accent
        renderPopoverContent={renderPopoverContent}
        showMask={createMasks}
        onChange={onChange}
        metricsIdentifier="callout-popover"
        forcePlacement={forcePlacement}
      >
        {({Anchor}) => (
          <>
            {asChild ? (
              <Anchor asChild>{children}</Anchor>
            ) : (
              <Anchor asChild={false}>
                {(anchorRef) => children(anchorRef)}
              </Anchor>
            )}
          </>
        )}
      </DeprecatedPopoverBridge>
    );
  }
);

const defaultMaskConfig = [{id: 'popover-mask', isMask: true}];

function getCanvasConfigs({
  // @ts-expect-error - TS7031 - Binding element 'canvas' implicitly has an 'any' type.
  canvas,
  // @ts-expect-error - TS7031 - Binding element 'hotspot' implicitly has an 'any' type.
  hotspot,
  // @ts-expect-error - TS7031 - Binding element 'anchor' implicitly has an 'any' type.
  anchor,
  // @ts-expect-error - TS7031 - Binding element 'mask' implicitly has an 'any' type.
  mask,
  // @ts-expect-error - TS7031 - Binding element 'canvasViewport' implicitly has an 'any' type.
  canvasViewport,
  // @ts-expect-error - TS7031 - Binding element 'parentBox' implicitly has an 'any' type.
  parentBox,
  // @ts-expect-error - TS7031 - Binding element 'onClick' implicitly has an 'any' type.
  onClick,
}) {
  const hasInteractiveAnchor = hotspot !== 'none';
  const isMask = mask !== 'TRANSPARENT';
  const defaultCanvasConfig = [
    {
      id: 'popover-mask',
      isMask,
      allowMouseEventsInWindow: hasInteractiveAnchor,
    },
  ];

  if (canvas === 'none') {
    return defaultCanvasConfig;
  }

  // Canvas box dimensions
  const cutouts = canvasViewport ? [canvasViewport] : [];
  const isAnchorIntersectingCanvas = canvasViewport
    ? collides(anchor, canvasViewport)
    : false;

  // Cut out the anchor from the canvas masks below to enable interactivity
  // if the anchor is intersecting with the Canvas
  if (isAnchorIntersectingCanvas || parentBox) {
    cutouts.push(anchor);
  }

  const configs = [];

  // Cuts out a parent box dimension of the anchor so part of the mask
  // is transparent, but still non-interactive
  if (parentBox) {
    cutouts.push(parentBox);
    configs.push({
      id: 'popover-parent-mask-cutout',
      viewport: parentBox,
      anchor: parentBox,
      isMask: false,
      transparentAnchor: true,
      style: hotspotStyle.transparent,
      onClick,
    });
  }

  // - Adds mask over left/right/top/canvas bars,
  // - Makes canvas transparent
  // - Allows pointer events to go through anchor (if interactive) AND canvas
  configs.push({
    id: 'popover-canvas-mask-cutout',
    isMask,
    allowMouseEventsInWindow: hasInteractiveAnchor,
    cutouts,
  });

  // - Adds a transparent mask over viewport (so mask darkness doesn't deepen)
  // - Allows pointer events on anchor (if interactive), but prevents pointer
  //   events on the canvas
  if (canvas === 'static' || canvas === 'scroll') {
    configs.push({
      id: 'popover-canvas-mask',
      isMask: false,
      allowMouseEventsInWindow: hasInteractiveAnchor,
    });
  }

  return configs.length === 0 ? defaultCanvasConfig : configs;
}

const hotspotStyle = {
  transparent: {fill: 'transparent', pointerEvents: 'none'},
  anchor: {
    fill: designerTheme.colors.accentMask,
    opacity: 0.3,
    pointerEvents: 'none',
  },
  border: {
    fill: 'transparent',
    border: `solid 2px ${designerTheme.colors.accentMask}`,
    borderRadius: 2,
    pointerEvents: 'none',
  },
} as const;

// @ts-expect-error - TS7031 - Binding element 'anchor' implicitly has an 'any' type. | TS7031 - Binding element 'animate' implicitly has an 'any' type. | TS7031 - Binding element 'hotspot' implicitly has an 'any' type. | TS7031 - Binding element 'hotspotConfig' implicitly has an 'any' type.
function getHotspotConfigs({anchor, animate, hotspot, hotspotConfig}) {
  // @ts-expect-error - TS7034 - Variable 'configs' implicitly has type 'any[]' in some locations where its type cannot be determined.
  const configs = [];
  const {style = null, ...config} = hotspotConfig;

  if (hotspot === 'none') {
    // @ts-expect-error - TS7005 - Variable 'configs' implicitly has an 'any[]' type.
    return configs;
  }

  configs.push({
    id: 'popover-hotspot',
    viewport: anchor,
    anchor,
    transparentAnchor: false,
    style: {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly transparent: { readonly fill: "transparent"; readonly pointerEvents: "none"; }; readonly anchor: { readonly fill: string; readonly opacity: 0.3; readonly pointerEvents: "none"; }; readonly border: { ...; }; }'.
      ...hotspotStyle[hotspot],
      ...style,
    },
    animate,
    ...config,
  });

  return configs;
}
